import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { adminApi } from '../../../api/admin.api';
import {
  NotificationConfigItem,
  NotificationSummaryItem,
  NotificationUserItem,
  ReEngagementDailySummaryItem,
  ReEngagementUserItem,
  TopActiveTargetItem,
} from '../../../types/Admin.api.type';
import { Card } from '../../ui/card';
import { Button } from '../../ui/button';
import { Modal, ModalHeader, ModalTitle, ModalContent } from '../../ui/modal';
import { ChevronLeft, ChevronRight, ShieldCheck, X } from 'lucide-react';

const STATUS_COLORS: Record<string, string> = {
  SUCCESS: 'bg-green-100 text-green-700',
  FAILED: 'bg-red-100 text-red-700',
};

type NotificationTab = 'config' | 'survey' | 'topActive' | 'reEngagement';

/**
 * [M-032] 2026-08-03 토스 정책 변경으로 기존 알림 캠페인 3종의 발송을 중단했다.
 * 조회는 그대로 살아 있으므로, 다음 작업자가 "왜 발송이 안 되나" 헤매지 않도록 상태를 표시한다.
 */
const StoppedBadge = () => (
  <span
    className="px-2 py-0.5 text-xs font-medium rounded-full bg-gray-100 text-gray-500"
    title="2026-08 토스 알림 정책 변경으로 발송이 중단됐어요. 조회는 그대로 볼 수 있어요."
  >
    발송 중단됨
  </span>
);

export const NotificationHistoryPanel = () => {
  const [activeTab, setActiveTab] = useState<NotificationTab>('survey');
  const [page, setPage] = useState(0);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState<Record<string, unknown> | null>(null);
  const [selectedQuestionId, setSelectedQuestionId] = useState<number | null>(null);
  const pageSize = 20;

  const handleVerify = async () => {
    setVerifying(true);
    setVerifyResult(null);
    try {
      const res = await adminApi.verifyUserKey();
      setVerifyResult(res.data);
    } catch (e: any) {
      setVerifyResult({ error: e.message });
    } finally {
      setVerifying(false);
    }
  };

  const { data, isLoading } = useQuery({
    queryKey: ['adminNotificationSummary', page],
    queryFn: () => adminApi.getNotificationSummary(page, pageSize).then((res) => res.data),
  });

  const { data: usersData, isLoading: usersLoading } = useQuery({
    queryKey: ['adminNotificationUsers', selectedQuestionId],
    queryFn: () => adminApi.getNotificationUsers(selectedQuestionId!).then((res) => res.data),
    enabled: selectedQuestionId != null,
  });

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const truncateTitle = (title: string, maxLen: number = 30) => {
    return title.length > maxLen ? title.substring(0, maxLen) + '...' : title;
  };

  return (
    <Card>
      <div className="p-6">
        {/* 탭 전환 */}
        <div className="flex items-center gap-2 mb-4 border-b pb-2">
          <button
            className={`px-3 py-1.5 text-sm rounded-t-lg ${activeTab === 'config' ? 'bg-gray-100 font-semibold text-gray-800' : 'text-gray-400 hover:text-gray-600'}`}
            onClick={() => setActiveTab('config')}
          >
            알림 설정
          </button>
          <button
            className={`px-3 py-1.5 text-sm rounded-t-lg ${activeTab === 'survey' ? 'bg-gray-100 font-semibold text-gray-800' : 'text-gray-400 hover:text-gray-600'}`}
            onClick={() => setActiveTab('survey')}
          >
            설문 결과 알림
          </button>
          <button
            className={`px-3 py-1.5 text-sm rounded-t-lg ${activeTab === 'topActive' ? 'bg-gray-100 font-semibold text-gray-800' : 'text-gray-400 hover:text-gray-600'}`}
            onClick={() => setActiveTab('topActive')}
          >
            활동점수 상위 재접속
          </button>
          <button
            className={`px-3 py-1.5 text-sm rounded-t-lg ${activeTab === 'reEngagement' ? 'bg-gray-100 font-semibold text-gray-800' : 'text-gray-400 hover:text-gray-600'}`}
            onClick={() => setActiveTab('reEngagement')}
          >
            재접속 유도
          </button>
        </div>

        {activeTab === 'config' ? (
          <NotificationConfigSection />
        ) : activeTab === 'reEngagement' ? (
          <ReEngagementSection />
        ) : activeTab === 'topActive' ? (
          <TopActiveSection />
        ) : (
          <>
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                <h3 className="text-sm font-semibold text-gray-700">알림 발송 이력</h3>
                <StoppedBadge />
                <Button variant="outline" size="sm" onClick={handleVerify} disabled={verifying}>
                  <span className="flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3" />
                    {verifying ? '확인 중...' : '유저키 검증'}
                  </span>
                </Button>
              </div>
            </div>

            {verifyResult && (
              <div
                className={`mb-4 p-3 rounded-lg text-sm ${verifyResult.error ? 'bg-yellow-50 text-yellow-700 border border-yellow-200' : verifyResult.match ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-700 border border-red-200'}`}
              >
                <div className="font-medium">
                  {verifyResult.error ? '검증 실패' : verifyResult.match ? 'UserKey 일치' : 'UserKey 불일치!'}
                </div>
                <div className="mt-1 text-xs space-y-0.5">
                  {'dbSocialId' in verifyResult && (
                    <div>
                      DB socialId: <code className="bg-white/50 px-1 rounded">{String(verifyResult.dbSocialId)}</code>
                    </div>
                  )}
                  {'apiUserKey' in verifyResult && (
                    <div>
                      API userKey: <code className="bg-white/50 px-1 rounded">{String(verifyResult.apiUserKey)}</code>
                    </div>
                  )}
                  {'error' in verifyResult && (
                    <div>
                      에러: <code className="bg-white/50 px-1 rounded">{String(verifyResult.error)}</code>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => setVerifyResult(null)}
                  className="mt-2 text-xs underline opacity-60 hover:opacity-100"
                >
                  닫기
                </button>
              </div>
            )}

            {isLoading ? (
              <div className="text-center py-8 text-gray-400">로딩 중...</div>
            ) : !data?.content?.length ? (
              <div className="text-center py-8 text-gray-400">발송 이력이 없습니다.</div>
            ) : (
              <>
                {/* 데스크톱 테이블 */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b text-left text-gray-500">
                        <th className="pb-2 pr-3">설문ID</th>
                        <th className="pb-2 pr-3">설문 제목</th>
                        <th className="pb-2 pr-3 text-center">발송</th>
                        <th className="pb-2 pr-3 text-center">성공</th>
                        <th className="pb-2 pr-3 text-center">실패</th>
                        <th className="pb-2">발송시각</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.content.map((item: NotificationSummaryItem) => (
                        <tr
                          key={item.questionId}
                          className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                          onClick={() => setSelectedQuestionId(item.questionId)}
                        >
                          <td className="py-2 pr-3 text-gray-500">{item.questionId}</td>
                          <td className="py-2 pr-3" title={item.questionTitle}>
                            {truncateTitle(item.questionTitle)}
                          </td>
                          <td className="py-2 pr-3 text-center font-medium">{item.totalCount}명</td>
                          <td className="py-2 pr-3 text-center">
                            <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                              {item.successCount}
                            </span>
                          </td>
                          <td className="py-2 pr-3 text-center">
                            {item.failedCount > 0 ? (
                              <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">
                                {item.failedCount}
                              </span>
                            ) : (
                              <span className="text-xs text-gray-300">0</span>
                            )}
                          </td>
                          <td className="py-2 text-gray-400 text-xs">{formatDate(item.sentAt)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* 모바일 카드 */}
                <div className="md:hidden space-y-3">
                  {data.content.map((item: NotificationSummaryItem) => (
                    <div
                      key={item.questionId}
                      className="border rounded-lg p-3 space-y-2 cursor-pointer hover:bg-gray-50"
                      onClick={() => setSelectedQuestionId(item.questionId)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium">설문 #{item.questionId}</span>
                        <span className="text-xs text-gray-400">{formatDate(item.sentAt)}</span>
                      </div>
                      <div className="text-xs text-gray-600 truncate">{item.questionTitle}</div>
                      <div className="flex items-center gap-2 text-xs">
                        <span className="text-gray-500">{item.totalCount}명</span>
                        <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700">
                          성공 {item.successCount}
                        </span>
                        {item.failedCount > 0 && (
                          <span className="px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                            실패 {item.failedCount}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* 페이지네이션 */}
                <div className="flex items-center justify-between mt-4 pt-3 border-t">
                  <span className="text-xs text-gray-500">총 {data.totalElements}건</span>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>
                      <ChevronLeft className="w-4 h-4" />
                    </Button>
                    <span className="text-sm text-gray-600">
                      {page + 1} / {data.totalPages || 1}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= (data.totalPages || 1) - 1}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </>
            )}

            {/* 발송 유저 목록 모달 */}
            <Modal
              isOpen={selectedQuestionId != null}
              onClose={() => setSelectedQuestionId(null)}
              className="max-w-2xl"
            >
              <ModalHeader>
                <div className="flex items-center justify-between">
                  <ModalTitle>설문 #{selectedQuestionId} 발송 유저 목록</ModalTitle>
                  <button onClick={() => setSelectedQuestionId(null)} className="text-gray-400 hover:text-gray-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </ModalHeader>
              <ModalContent>
                {usersLoading ? (
                  <div className="text-center py-8 text-gray-400">로딩 중...</div>
                ) : !usersData?.length ? (
                  <div className="text-center py-8 text-gray-400">발송 유저가 없습니다.</div>
                ) : (
                  <div className="space-y-1">
                    <div className="text-xs text-gray-500 mb-3">총 {usersData.length}명</div>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-gray-500">
                          <th className="pb-2 pr-3">유저명</th>
                          <th className="pb-2 pr-3">최종 접속일</th>
                          <th className="pb-2 pr-3">상태</th>
                          <th className="pb-2">에러</th>
                        </tr>
                      </thead>
                      <tbody>
                        {usersData.map((user: NotificationUserItem, idx: number) => (
                          <tr key={idx} className="border-b last:border-0">
                            <td className="py-2 pr-3">{user.nickname}</td>
                            <td className="py-2 pr-3 text-xs text-gray-400">{user.lastVisitDate ?? '-'}</td>
                            <td className="py-2 pr-3">
                              <span
                                className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[user.status] ?? 'bg-gray-100 text-gray-500'}`}
                              >
                                {user.status === 'SUCCESS' ? '성공' : '실패'}
                              </span>
                            </td>
                            <td
                              className="py-2 text-xs text-red-500 max-w-[200px] truncate"
                              title={user.errorMessage ?? undefined}
                            >
                              {user.status === 'FAILED' && user.errorMessage ? user.errorMessage : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </ModalContent>
            </Modal>
          </>
        )}
      </div>
    </Card>
  );
};

/** 알림 설정 관리 */
const NotificationConfigSection = () => {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<Record<number, Partial<NotificationConfigItem>>>({});

  const { data: configs, isLoading } = useQuery({
    queryKey: ['notificationConfigs'],
    queryFn: () => adminApi.getNotificationConfigs().then((res) => res.data),
  });

  const handleToggle = async (config: NotificationConfigItem) => {
    await adminApi.updateNotificationConfig(config.id, { enabled: !config.enabled });
    queryClient.invalidateQueries({ queryKey: ['notificationConfigs'] });
  };

  const handleSave = async (config: NotificationConfigItem) => {
    const edit = editing[config.id];
    if (!edit) return;
    await adminApi.updateNotificationConfig(config.id, edit);
    setEditing((prev) => {
      const next = { ...prev };
      delete next[config.id];
      return next;
    });
    queryClient.invalidateQueries({ queryKey: ['notificationConfigs'] });
  };

  if (isLoading) return <div className="text-center py-8 text-gray-400">로딩 중...</div>;
  if (!configs?.length) return <div className="text-center py-8 text-gray-400">알림 설정이 없습니다.</div>;

  const TYPE_LABELS: Record<string, string> = {
    SURVEY_RESULT: '설문 종료 결과 알림',
    RE_ENGAGEMENT: '재접속 유도 알림',
    TOP_ACTIVE_RE_ENGAGEMENT: '활동점수 상위 재접속 알림',
  };

  return (
    <>
      <div className="flex items-center gap-3 mb-3">
        <h3 className="text-sm font-semibold text-gray-700">알림 발송 설정</h3>
        <StoppedBadge />
      </div>
      <div className="space-y-3">
        {configs.map((config: NotificationConfigItem) => {
          const edit = editing[config.id];
          const isReEngagement = config.notificationType === 'RE_ENGAGEMENT';
          // 스케줄 필드(발송시간/미접속/쿨다운/발송인원)는 신규 활동점수 캠페인도 편집 가능
          const hasScheduleFields = isReEngagement || config.notificationType === 'TOP_ACTIVE_RE_ENGAGEMENT';
          return (
            <div key={config.id} className="border rounded-lg p-4">
              <div className="flex items-center justify-between mb-3">
                <div>
                  <span className="font-medium text-sm">
                    {TYPE_LABELS[config.notificationType] ?? config.notificationType}
                  </span>
                  {config.description && <span className="text-xs text-gray-400 ml-2">{config.description}</span>}
                </div>
                <button
                  onClick={() => handleToggle(config)}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.enabled ? 'bg-blue-500' : 'bg-gray-300'}`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.enabled ? 'translate-x-6' : 'translate-x-1'}`}
                  />
                </button>
              </div>
              <div className="flex items-center gap-2 mb-2">
                <label className="text-xs text-gray-500 shrink-0">템플릿 코드:</label>
                <input
                  type="text"
                  defaultValue={config.templateCode}
                  onChange={(e) =>
                    setEditing((prev) => ({
                      ...prev,
                      [config.id]: { ...prev[config.id], templateCode: e.target.value },
                    }))
                  }
                  className="flex-1 text-xs border rounded px-2 py-1 font-mono"
                />
              </div>
              {hasScheduleFields && (
                <>
                  <div className="flex items-center gap-2 mb-2">
                    <label className="text-xs text-gray-500 shrink-0">발송 시간:</label>
                    <select
                      defaultValue={config.sendHour}
                      onChange={(e) =>
                        setEditing((prev) => ({
                          ...prev,
                          [config.id]: { ...prev[config.id], sendHour: Number(e.target.value) },
                        }))
                      }
                      className="text-xs border rounded px-2 py-1"
                    >
                      {Array.from({ length: 24 }, (_, i) => (
                        <option key={i} value={i}>
                          {String(i).padStart(2, '0')}:00
                        </option>
                      ))}
                    </select>
                    <span className="text-xs text-gray-400">매일 이 시간에 자동 발송</span>
                  </div>
                  <div className="flex items-center gap-4 mb-2">
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-500 shrink-0">미접속 기준:</label>
                      <input
                        type="number"
                        min={1}
                        defaultValue={config.inactiveDays}
                        onChange={(e) =>
                          setEditing((prev) => ({
                            ...prev,
                            [config.id]: { ...prev[config.id], inactiveDays: Number(e.target.value) },
                          }))
                        }
                        className="w-16 text-xs border rounded px-2 py-1 text-right"
                      />
                      <span className="text-xs text-gray-400">일</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-500 shrink-0">쿨다운:</label>
                      <input
                        type="number"
                        min={1}
                        defaultValue={config.cooldownDays}
                        onChange={(e) =>
                          setEditing((prev) => ({
                            ...prev,
                            [config.id]: { ...prev[config.id], cooldownDays: Number(e.target.value) },
                          }))
                        }
                        className="w-16 text-xs border rounded px-2 py-1 text-right"
                      />
                      <span className="text-xs text-gray-400">일</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <label className="text-xs text-gray-500 shrink-0">발송 인원:</label>
                      <input
                        type="number"
                        min={1}
                        defaultValue={config.batchSize}
                        onChange={(e) =>
                          setEditing((prev) => ({
                            ...prev,
                            [config.id]: { ...prev[config.id], batchSize: Number(e.target.value) },
                          }))
                        }
                        className="w-16 text-xs border rounded px-2 py-1 text-right"
                      />
                      <span className="text-xs text-gray-400">명</span>
                    </div>
                  </div>
                </>
              )}
              <div className="flex items-center gap-2">
                {edit && (
                  <Button variant="outline" size="sm" onClick={() => handleSave(config)}>
                    저장
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
};

/** 재접속 유도 알림 섹션 */
const ReEngagementSection = () => {
  const [selectedSentAt, setSelectedSentAt] = useState<string | null>(null);

  const { data: summary, isLoading } = useQuery({
    queryKey: ['reEngagementSummary'],
    queryFn: () => adminApi.getReEngagementSummary().then((res) => res.data),
  });

  const {
    data: users,
    isLoading: usersLoading,
    isError: usersError,
  } = useQuery({
    queryKey: ['reEngagementUsers', selectedSentAt],
    queryFn: () => adminApi.getReEngagementUsers(selectedSentAt!).then((res) => res.data),
    enabled: selectedSentAt != null,
  });

  if (isLoading) return <div className="text-center py-8 text-gray-400">로딩 중...</div>;

  return (
    <>
      <div className="flex items-center gap-3 mb-3">
        <h3 className="text-sm font-semibold text-gray-700">재접속 유도 일별 발송</h3>
        <StoppedBadge />
      </div>

      {!summary?.length ? (
        <div className="text-center py-8 text-gray-400">재접속 유도 발송 이력이 없습니다.</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-500">
                  <th className="pb-2 pr-3">발송일시</th>
                  <th className="pb-2 pr-3 text-center">발송</th>
                  <th className="pb-2 pr-3 text-center">성공</th>
                  <th className="pb-2 pr-3 text-center">실패</th>
                  <th className="pb-2 pr-3 text-center">복귀</th>
                  <th className="pb-2 text-center">복귀율</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((item: ReEngagementDailySummaryItem) => {
                  const returnRate =
                    item.successCount > 0 ? Math.round((item.returnedCount / item.successCount) * 100) : 0;
                  const sentDate = item.sentAt?.slice(0, 10) ?? '';
                  const sentHour = item.sentAt ? new Date(item.sentAt).getHours() + '시' : '';
                  return (
                    <tr
                      key={item.sentAt}
                      className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedSentAt(item.sentAt)}
                    >
                      <td className="py-2 pr-3">
                        {sentDate} <span className="text-gray-400 text-xs">{sentHour}</span>
                      </td>
                      <td className="py-2 pr-3 text-center font-medium">{item.totalCount}명</td>
                      <td className="py-2 pr-3 text-center">
                        <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                          {item.successCount}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-center">
                        {item.failedCount > 0 ? (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">
                            {item.failedCount}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-300">0</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-center">
                        <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">
                          {item.returnedCount}
                        </span>
                      </td>
                      <td className="py-2 text-center">
                        <span className={`text-xs font-medium ${returnRate >= 10 ? 'text-blue-600' : 'text-gray-500'}`}>
                          {returnRate}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* 유저 상세 모달 */}
          <Modal isOpen={selectedSentAt != null} onClose={() => setSelectedSentAt(null)} className="max-w-3xl">
            <ModalHeader>
              <div className="flex items-center justify-between">
                <ModalTitle>
                  {selectedSentAt
                    ? `${selectedSentAt.slice(0, 10)} ${new Date(selectedSentAt).getHours()}시 발송 유저 목록`
                    : '발송 유저 목록'}
                </ModalTitle>
                <button onClick={() => setSelectedSentAt(null)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-4 h-4" />
                </button>
              </div>
            </ModalHeader>
            <ModalContent>
              {usersLoading ? (
                <div className="text-center py-8 text-gray-400">로딩 중...</div>
              ) : usersError ? (
                <div className="text-center py-8 text-red-400">발송 유저를 불러오지 못했어요.</div>
              ) : !users?.length ? (
                <div className="text-center py-8 text-gray-400">발송 유저가 없습니다.</div>
              ) : (
                <div>
                  <div className="text-xs text-gray-500 mb-3">
                    총 {users.length}명 | 복귀 {users.filter((u: ReEngagementUserItem) => u.returned).length}명
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b text-left text-gray-500">
                          <th className="pb-2 pr-3">유저명</th>
                          <th className="pb-2 pr-3 text-center">보유조각</th>
                          <th className="pb-2 pr-3">발송전 최종접속</th>
                          <th className="pb-2 pr-3">발송후 접속</th>
                          <th className="pb-2 pr-3 text-center">복귀</th>
                          <th className="pb-2 pr-3">상태</th>
                          <th className="pb-2">에러</th>
                        </tr>
                      </thead>
                      <tbody>
                        {users.map((user: ReEngagementUserItem, idx: number) => (
                          <tr key={idx} className="border-b last:border-0">
                            <td className="py-2 pr-3">{user.nickname}</td>
                            <td className="py-2 pr-3 text-center text-xs">{user.balance}개</td>
                            <td className="py-2 pr-3 text-xs text-gray-400">{user.lastVisitBeforeSend ?? '-'}</td>
                            <td className="py-2 pr-3 text-xs text-gray-400">{user.lastVisitAfterSend ?? '-'}</td>
                            <td className="py-2 pr-3 text-center">
                              {user.returned ? (
                                <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">복귀</span>
                              ) : (
                                <span className="text-xs text-gray-300">-</span>
                              )}
                            </td>
                            <td className="py-2 pr-3">
                              <span
                                className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[user.status] ?? 'bg-gray-100 text-gray-500'}`}
                              >
                                {user.status === 'SUCCESS' ? '성공' : '실패'}
                              </span>
                            </td>
                            <td
                              className="py-2 text-xs text-red-500 max-w-[150px] truncate"
                              title={user.errorMessage ?? undefined}
                            >
                              {user.status === 'FAILED' && user.errorMessage ? user.errorMessage : '-'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </ModalContent>
          </Modal>
        </>
      )}
    </>
  );
};

/** M-016 활동점수 상위 재접속 알림 섹션 */
const TopActiveSection = () => {
  const [selectedBatchHour, setSelectedBatchHour] = useState<string | null>(null);

  const { data: preview, isLoading } = useQuery({
    queryKey: ['topActivePreview'],
    queryFn: () => adminApi.getTopActivePreview().then((res) => res.data),
  });

  const {
    data: summary,
    isLoading: summaryLoading,
    isError: summaryError,
  } = useQuery({
    queryKey: ['topActiveSummary'],
    queryFn: () => adminApi.getTopActiveSummary().then((res) => res.data),
  });

  const {
    data: users,
    isLoading: usersLoading,
    isError: usersError,
  } = useQuery({
    queryKey: ['topActiveUsers', selectedBatchHour],
    queryFn: () => adminApi.getTopActiveUsers(selectedBatchHour!).then((res) => res.data),
    enabled: selectedBatchHour != null,
  });

  const targets = preview?.targets ?? [];

  return (
    <>
      <div className="flex items-center gap-3 mb-3">
        <h3 className="text-sm font-semibold text-gray-700">발송 대상 미리보기</h3>
        <StoppedBadge />
      </div>

      <p className="text-xs text-gray-400 mb-3">
        7일 연속 미접속자 중 활동점수 상위 대상이에요. 발송은 중단됐고, 대상 목록은 참고용으로만 볼 수 있어요.
      </p>

      {isLoading ? (
        <div className="text-center py-8 text-gray-400">로딩 중...</div>
      ) : targets.length === 0 ? (
        <div className="text-center py-8 text-gray-400">발송 대상이 없어요.</div>
      ) : (
        <div className="overflow-x-auto">
          <div className="text-xs text-gray-500 mb-2">총 {targets.length}명</div>
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-left text-gray-500">
                <th className="pb-2 pr-3">유저명</th>
                <th className="pb-2 pr-3 text-right">활동점수</th>
                <th className="pb-2">최종 접속일</th>
              </tr>
            </thead>
            <tbody>
              {targets.map((t: TopActiveTargetItem) => (
                <tr key={t.userId} className="border-b last:border-0 hover:bg-gray-50">
                  <td className="py-2 pr-3">{t.nickname}</td>
                  <td className="py-2 pr-3 text-right font-medium">{t.activityScore}</td>
                  <td className="py-2 text-xs text-gray-400">{t.lastVisitDate ?? '방문 이력 없음'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* 발송 이력 + 복귀 */}
      <div className="mt-8 pt-6 border-t">
        <h3 className="text-sm font-semibold text-gray-700 mb-3">발송 이력</h3>
        {summaryLoading ? (
          <div className="text-center py-8 text-gray-400">로딩 중...</div>
        ) : summaryError ? (
          <div className="text-center py-8 text-red-400">발송 이력을 불러오지 못했어요.</div>
        ) : !summary?.length ? (
          <div className="text-center py-8 text-gray-400">발송 이력이 없어요.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-gray-500">
                  <th className="pb-2 pr-3">발송일시</th>
                  <th className="pb-2 pr-3 text-center">발송</th>
                  <th className="pb-2 pr-3 text-center">성공</th>
                  <th className="pb-2 pr-3 text-center">실패</th>
                  <th className="pb-2 pr-3 text-center">복귀</th>
                  <th className="pb-2 text-center">복귀율</th>
                </tr>
              </thead>
              <tbody>
                {summary.map((item: ReEngagementDailySummaryItem) => {
                  const returnRate =
                    item.successCount > 0 ? Math.round((item.returnedCount / item.successCount) * 100) : 0;
                  const sentDate = item.sentAt?.slice(0, 10) ?? '';
                  const sentHour = item.sentAt ? new Date(item.sentAt).getHours() + '시' : '';
                  return (
                    <tr
                      key={item.sentAt}
                      className="border-b last:border-0 hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedBatchHour(item.sentAt ?? null)}
                    >
                      <td className="py-2 pr-3">
                        {sentDate} <span className="text-gray-400 text-xs">{sentHour}</span>
                      </td>
                      <td className="py-2 pr-3 text-center font-medium">{item.totalCount}명</td>
                      <td className="py-2 pr-3 text-center">
                        <span className="px-2 py-0.5 rounded-full text-xs bg-green-100 text-green-700">
                          {item.successCount}
                        </span>
                      </td>
                      <td className="py-2 pr-3 text-center">
                        {item.failedCount > 0 ? (
                          <span className="px-2 py-0.5 rounded-full text-xs bg-red-100 text-red-700">
                            {item.failedCount}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-300">0</span>
                        )}
                      </td>
                      <td className="py-2 pr-3 text-center">
                        <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">
                          {item.returnedCount}
                        </span>
                      </td>
                      <td className="py-2 text-center">
                        <span className={`text-xs font-medium ${returnRate >= 10 ? 'text-blue-600' : 'text-gray-500'}`}>
                          {returnRate}%
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* 발송 유저 상세 모달 */}
      <Modal isOpen={selectedBatchHour != null} onClose={() => setSelectedBatchHour(null)} className="max-w-3xl">
        <ModalHeader>
          <div className="flex items-center justify-between">
            <ModalTitle>
              {selectedBatchHour
                ? `${selectedBatchHour.slice(0, 10)} ${new Date(selectedBatchHour).getHours()}시 발송 유저 목록`
                : '발송 유저 목록'}
            </ModalTitle>
            <button onClick={() => setSelectedBatchHour(null)} className="text-gray-400 hover:text-gray-600">
              <X className="w-4 h-4" />
            </button>
          </div>
        </ModalHeader>
        <ModalContent>
          {usersLoading ? (
            <div className="text-center py-8 text-gray-400">로딩 중...</div>
          ) : usersError ? (
            <div className="text-center py-8 text-red-400">발송 유저를 불러오지 못했어요.</div>
          ) : !users?.length ? (
            <div className="text-center py-8 text-gray-400">발송 유저가 없어요.</div>
          ) : (
            <div>
              <div className="text-xs text-gray-500 mb-3">
                총 {users.length}명 | 복귀 {users.filter((u: ReEngagementUserItem) => u.returned).length}명
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b text-left text-gray-500">
                      <th className="pb-2 pr-3">유저명</th>
                      <th className="pb-2 pr-3 text-center">보유조각</th>
                      <th className="pb-2 pr-3">발송전 최종접속</th>
                      <th className="pb-2 pr-3">발송후 접속</th>
                      <th className="pb-2 pr-3 text-center">복귀</th>
                      <th className="pb-2 pr-3">상태</th>
                      <th className="pb-2">에러</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((user: ReEngagementUserItem, idx: number) => (
                      <tr key={idx} className="border-b last:border-0">
                        <td className="py-2 pr-3">{user.nickname}</td>
                        <td className="py-2 pr-3 text-center text-xs">{user.balance}개</td>
                        <td className="py-2 pr-3 text-xs text-gray-400">{user.lastVisitBeforeSend ?? '-'}</td>
                        <td className="py-2 pr-3 text-xs text-gray-400">{user.lastVisitAfterSend ?? '-'}</td>
                        <td className="py-2 pr-3 text-center">
                          {user.returned ? (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-700">복귀</span>
                          ) : (
                            <span className="text-xs text-gray-300">-</span>
                          )}
                        </td>
                        <td className="py-2 pr-3">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs ${STATUS_COLORS[user.status] ?? 'bg-gray-100 text-gray-500'}`}
                          >
                            {user.status === 'SUCCESS' ? '성공' : '실패'}
                          </span>
                        </td>
                        <td
                          className="py-2 text-xs text-red-500 max-w-[150px] truncate"
                          title={user.errorMessage ?? undefined}
                        >
                          {user.status === 'FAILED' && user.errorMessage ? user.errorMessage : '-'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
